/**
 * Чистая логика Центра внимания: раскладка по блокам, порядок, сроки,
 * состояние бейджа и варианты «отложить».
 *
 * Здесь нет ни React, ни сети — только вычисления над карточками, которые
 * пришли с сервера. Смысл события (`attention_kind`, `reason`, русское имя
 * категории) считает backend; фронт лишь показывает то, что получил.
 */

export const ACTION_REQUIRED = 'action_required'
export const UPDATE = 'update'
export const DIGEST = 'digest'
export const MENTION = 'mention'

const DAY_MS = 24 * 60 * 60 * 1000

/** Идёт ли карточка в блок «Требует действия». */
export function isActionable(n) {
    if (!n) return false
    if (n.priority === 'critical') return true
    return n.attention_kind === ACTION_REQUIRED || n.attention_kind === MENTION
}

/** Разложить ленту на два смысловых блока, сохранив исходный порядок. */
export function splitByAttention(items = []) {
    const action = []
    const updates = []
    for (const n of items) {
        if (n?.is_archived) continue
        ;(isActionable(n) ? action : updates).push(n)
    }
    return { action, updates }
}

/**
 * Ранг внутри блока действий: просрочка → critical → ближайший дедлайн →
 * high → остальное. Зеркалит серверный `order=attention`, чтобы карточка,
 * прилетевшая по WebSocket, встала на то же место, что и после перезагрузки.
 */
export function attentionRank(n, now = Date.now()) {
    const deadline = n?.deadline_at ? new Date(n.deadline_at).getTime() : null
    if (deadline && deadline < now) return 0
    if (n?.priority === 'critical') return 1
    if (deadline) return 2
    if (n?.priority === 'high') return 3
    return 4
}

export function sortAttention(items = [], now = Date.now()) {
    return [...items].sort((a, b) => {
        const rank = attentionRank(a, now) - attentionRank(b, now)
        if (rank !== 0) return rank
        const da = a?.deadline_at ? new Date(a.deadline_at).getTime() : Infinity
        const db = b?.deadline_at ? new Date(b.deadline_at).getTime() : Infinity
        if (da !== db) return da - db
        return new Date(b?.created_at || 0) - new Date(a?.created_at || 0)
    })
}

export function sortRecent(items = []) {
    return [...items].sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))
}

// ── Сроки ────────────────────────────────────────────────────────

function pluralDays(count) {
    const abs = Math.abs(count) % 100
    const tail = abs % 10
    if (abs > 10 && abs < 20) return 'дней'
    if (tail === 1) return 'день'
    if (tail >= 2 && tail <= 4) return 'дня'
    return 'дней'
}

function startOfDay(ts) {
    const d = new Date(ts)
    d.setHours(0, 0, 0, 0)
    return d.getTime()
}

function hhmm(date) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

/**
 * Человеческий текст срока: «до сегодня 16:00», «осталось 2 дня»,
 * «просрочено на 1 день». Возвращает null, если срока нет.
 */
export function deadlineLabel(deadlineAt, now = Date.now()) {
    if (!deadlineAt) return null
    const due = new Date(deadlineAt)
    const dueTs = due.getTime()
    if (Number.isNaN(dueTs)) return null

    const dayDiff = Math.round((startOfDay(dueTs) - startOfDay(now)) / DAY_MS)

    if (dueTs < now) {
        const overdue = Math.abs(dayDiff)
        return overdue === 0 ? 'просрочено сегодня' : `просрочено на ${overdue} ${pluralDays(overdue)}`
    }
    if (dayDiff === 0) return `до сегодня ${hhmm(due)}`
    if (dayDiff === 1) return `до завтра ${hhmm(due)}`
    return `осталось ${dayDiff} ${pluralDays(dayDiff)}`
}

export function isOverdue(n, now = Date.now()) {
    if (!n?.deadline_at) return false
    const ts = new Date(n.deadline_at).getTime()
    return !Number.isNaN(ts) && ts < now
}

// ── Бейдж колокольчика ───────────────────────────────────────────

/**
 * Что рисовать на колокольчике. Число — только про «прервись и сделай»;
 * новые FYI дают тихую точку, а не тревожную цифру.
 */
export function bellState(summary) {
    const attention = Number(summary?.attention_unread ?? 0)
    const updates = Number(summary?.updates_unread ?? 0)
    const critical = Number(summary?.critical_unread ?? 0)
    return {
        count: attention,
        label: attention > 99 ? '99+' : String(attention),
        showCount: attention > 0,
        showDot: attention === 0 && updates > 0,
        critical: critical > 0,
    }
}

/** Считает сводку из локального списка — нужен, пока не приехал ответ сервера. */
export function summarizeLocally(items = []) {
    let attention = 0
    let updates = 0
    let critical = 0
    for (const n of items) {
        if (!n || n.is_read || n.is_archived) continue
        if (n.snoozed_until && new Date(n.snoozed_until).getTime() > Date.now()) continue
        if (isActionable(n)) attention += 1
        else updates += 1
        if (n.priority === 'critical') critical += 1
    }
    return {
        attention_unread: attention,
        updates_unread: updates,
        total_unread: attention + updates,
        critical_unread: critical,
    }
}

/**
 * Прерывать ли работу всплывающим окном. Обычное FYI приходит тихо в
 * колокольчик — иначе toast обесценивается и его перестают читать.
 */
export function shouldInterrupt(n) {
    if (!n) return false
    if (n.priority === 'critical' || n.priority === 'high') return true
    return n.attention_kind === ACTION_REQUIRED || n.attention_kind === MENTION
}

// ── «Отложить» ───────────────────────────────────────────────────

const EVENING_HOUR = 18
const MORNING_HOUR = 9

function atHour(base, hour, addDays = 0) {
    const d = new Date(base)
    d.setDate(d.getDate() + addDays)
    d.setHours(hour, 0, 0, 0)
    return d
}

function nextWorkdayMorning(base) {
    let d = atHour(base, MORNING_HOUR, 1)
    while (d.getDay() === 0 || d.getDay() === 6) d = atHour(d, MORNING_HOUR, 1)
    return d
}

/**
 * Варианты «отложить» в местном времени пользователя. Момент считает браузер:
 * «завтра утром» у человека и у сервера — разные часы.
 */
export function snoozePresets(now = new Date()) {
    const base = new Date(now)
    const presets = [
        { key: 'hour', label: 'Через час', until: new Date(base.getTime() + 60 * 60 * 1000) },
    ]
    const evening = atHour(base, EVENING_HOUR)
    if (evening.getTime() > base.getTime() + 30 * 60 * 1000) {
        presets.push({ key: 'evening', label: 'Сегодня позже', until: evening })
    }
    presets.push({ key: 'tomorrow', label: 'Завтра утром', until: atHour(base, MORNING_HOUR, 1) })
    const workday = nextWorkdayMorning(base)
    if (workday.getTime() !== atHour(base, MORNING_HOUR, 1).getTime()) {
        presets.push({ key: 'workday', label: 'В следующий рабочий день', until: workday })
    }
    return presets
}
